import React, { useState, useEffect, useMemo } from "react";
import CameraModal from "./CameraModal";

import CropImage from "./image_edit/CropImage";
import {
  Box,
  Button,
  Typography,
  Stack,
  IconButton,
  Chip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { AnimatePresence, motion } from "framer-motion";
import FilterModal from "./filter_modal/filters";
import ClientSelect from "./client_select";
import RemoveBackground from "./remove_background";
import CatalogCategorySelect from "./catalog_category_select";
import { CATEGORY_DATA } from "../enums/categoryData";
import { isOnnmeSkuPrefix } from "../enums/skuPrefixes";

// Define filter options and allowed values per client
const CLIENT_FILTERS = {
  giva: {
    category: {
      label: "Category",
      icon: <FilterAltIcon fontSize="small" />,
      getOptions: (catalogCategory) => {
        // Giva uses CATEGORY_DATA and catalogCategory
        const allCategories = Object.entries(CATEGORY_DATA)
          .map(([code, item]) => `${item.category} - ${code}`)
          .sort();
        if (catalogCategory === "gold") {
          return allCategories.filter((cat) =>
            cat.split(" - ").pop().startsWith("GD")
          );
        }
        if (catalogCategory === "silver") {
          return allCategories.filter(
            (cat) => !cat.split(" - ").pop().startsWith("GD")
          );
        }
        if (catalogCategory === "onnme") {
          return allCategories.filter((cat) =>
            isOnnmeSkuPrefix(cat.split(" - ").pop())
          );
        }
        return allCategories;
      },
    },
    status: {
      label: "Status",
      icon: <FilterAltIcon fontSize="small" />,
      options: ["active", "archived", "draft"],
    },
    search_text: {
      label: "Search Text",
      icon: <FilterAltIcon fontSize="small" />,
      type: "string",
    },
    // Add more Giva filters if needed
  },
  caratlane: {
    // metal: {
    //   label: "Metal",
    //   icon: <FilterAltIcon fontSize="small" />,
    //   options: ["Gold", "Silver", "Platinum"],
    // },
    // status: {
    //   label: "Status",
    //   icon: <FilterAltIcon fontSize="small" />,
    //   options: ["active", "archived"], // Only two allowed for caratlane
    // },
    // Add more CaratLane filters if needed
  },
  // Add more clients as needed
};

function ImageUploader({
  file,
  setFile,
  previewUrl,
  setPreviewUrl,
  catalogCategory,
  setCatalogCategory,
  client,
  setClient,
  filters = {},
  setFilters,
  hasSearched,
  setHasSearched,
  removeBackground,
  setRemoveBackground,
}) {
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isCameraModalOpen, setIsCameraModalOpen] = useState(false);
  const [storedCategories, setStoredCategories] = useState([]);
  const [showCropper, setShowCropper] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState(null);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMobileDevice = useMemo(
    () => /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent),
    []
  );

  // Memoize current client filter config
  const clientFilterConfig = useMemo(() => CLIENT_FILTERS[client] || CLIENT_FILTERS["giva"], [client]);

  // Build filterOptions for FilterModal
  const filterOptions = useMemo(() => {
    const opts = {};
    Object.entries(clientFilterConfig).forEach(([key, config]) => {
      opts[key] = {
        label: config.label,
        icon: config.icon,
        options: typeof config.getOptions === "function"
          ? config.getOptions(catalogCategory)
          : config.options,
        type: config.type,
      };
    });
    return opts;
  }, [clientFilterConfig, catalogCategory]);

  const handleFileSelect = (selectedFile) => {
    if (!selectedFile) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      setCropImageSrc(reader.result);
      setShowCropper(true); // Directly show cropper
    };
    reader.readAsDataURL(selectedFile);
  };

  const handleFileChange = (e) => {
    handleFileSelect(e.target.files[0]);
    setHasSearched(false);
  };

  const handleCameraCapture = (imageSrc) => {
    fetch(imageSrc)
      .then((res) => res.blob())
      .then((blob) => {
        const file = new File([blob], "captured.jpg", { type: "image/jpeg" });
        handleFileSelect(file);
      });
  };

  const handleCropUpload = (blob) => {
    const croppedFile = new File([blob], `image${Date.now()}.jpg`, {
      type: "image/jpeg",
    });
    setFile(croppedFile);
    setPreviewUrl(URL.createObjectURL(croppedFile));
    setShowCropper(false);
  };

  const handleCropCancel = () => {
    setShowCropper(false);
    setCropImageSrc(null);
  };

  const handleApplyFilters = (newFilters) => {
    // For Giva, handle sku_pref/category logic
    if (client === "giva") {
      setStoredCategories(newFilters.sku_pref || []);
      if (newFilters.sku_pref) {
        const filteredCategories =
          catalogCategory === "gold"
            ? newFilters.sku_pref.filter((code) => code.startsWith("GD"))
            : catalogCategory === "silver"
            ? newFilters.sku_pref.filter((code) => !code.startsWith("GD"))
            : catalogCategory === "onnme"
            ? newFilters.sku_pref.filter((code) => isOnnmeSkuPrefix(code))
            : newFilters.sku_pref;
        setFilters({
          ...newFilters,
          sku_pref: filteredCategories,
        });
      } else {
        setFilters(newFilters);
      }
    } else {
      setFilters(newFilters);
    }
  };

  const handleClearFilters = () => {
    setFilters({});
  };

  const activeFiltersCount = Object.keys(filters).filter(
    (key) =>
      filters[key] !== "" && filters[key] !== null && filters[key] !== undefined
  ).length;

  useEffect(() => {
    handleClearFilters();
    // Optionally reset catalogCategory if not giva
    if (client !== "giva") {
      setCatalogCategory(null);
    }
    // eslint-disable-next-line
  }, [client]);
  
  // Only run this effect for Giva
  useEffect(() => {
    if (client !== "giva") return;
    setFilters((prev) => {
      if (!prev.sku_pref) return prev;
      const categoriesToFilter =
        storedCategories.length > 0 ? storedCategories : prev.sku_pref;
      let validCategories;
      if (catalogCategory === "gold") {
        validCategories = categoriesToFilter.filter((code) =>
          code.startsWith("GD")
        );
      } else if (catalogCategory === "silver") {
        validCategories = categoriesToFilter.filter(
          (code) => !code.startsWith("GD")
        );
      } else if (catalogCategory === "onnme") {
        validCategories = categoriesToFilter.filter((code) =>
          isOnnmeSkuPrefix(code)
        );
      } else {
        validCategories = storedCategories;
      }
      return { ...prev, sku_pref: validCategories };
    });
  }, [catalogCategory, storedCategories, client]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      {showCropper && cropImageSrc && (
        <CropImage
          src={cropImageSrc}
          onUpload={handleCropUpload}
          onCancel={handleCropCancel}
        />
      )}

      <Stack
        direction={isSmallScreen ? "column" : "row"}
        spacing={isSmallScreen ? 1.25 : 1}
        alignItems={isSmallScreen ? "stretch" : "center"}
        width="100%"
      >
        <Box width="100%">
          <AnimatePresence mode="wait">
            {!file ? (
              <motion.div
                key="select-capture"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
                style={{
                  display: "flex",
                  width: "100%",
                  gap: isSmallScreen ? 8 : 12,
                  flexDirection: isSmallScreen ? "column" : "row",
                }}
              >
                <Button
                  variant="outlined"
                  component="label"
                  fullWidth
                  sx={{
                    textAlign: "left",
                    justifyContent: "flex-start",
                    pl: 2,
                    mr: isSmallScreen ? 0 : 1,
                    fontSize: "0.85rem",
                    minHeight: isSmallScreen ? 44 : 34,
                    fontWeight: 500,
                  }}
                >
                  Select Image
                  <input
                    type="file"
                    hidden
                    onChange={handleFileChange}
                    accept="image/*"
                  />
                </Button>
                {isMobileDevice ? (
                  <Button
                    variant="outlined"
                    component="label"
                    fullWidth
                    sx={{
                      textAlign: "left",
                      justifyContent: "flex-start",
                      pl: 2,
                      fontSize: "0.85rem",
                      minHeight: isSmallScreen ? 44 : 34,
                      fontWeight: 500,
                    }}
                  >
                    Capture Image
                    <input
                      type="file"
                      hidden
                      onChange={handleFileChange}
                      accept="image/*"
                      capture="environment"
                    />
                  </Button>
                ) : (
                  <Button
                    variant="outlined"
                    fullWidth
                    sx={{
                      textAlign: "left",
                      justifyContent: "flex-start",
                      pl: 2,
                      fontSize: "0.85rem",
                      minHeight: isSmallScreen ? 44 : 34,
                      fontWeight: 500,
                    }}
                    onClick={() => setIsCameraModalOpen(true)}
                  >
                    Capture Image
                  </Button>
                )}
              </motion.div>
            ) : (
              <motion.div
                key="file-selected"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
                style={{
                  display: "flex",
                  width: "100%",
                  gap: isSmallScreen ? 8 : 12,
                  flexDirection: isSmallScreen ? "column" : "row",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    width: "100%",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Button
                    variant="outlined"
                    fullWidth
                    sx={{
                      textAlign: "left",
                      justifyContent: "flex-start",
                      pl: 2,
                      fontSize: "0.85rem",
                      minHeight: isSmallScreen ? 48 : 40,
                      fontWeight: 500,
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        borderColor: "error.main",
                        backgroundColor: "error.light",
                        color: "error.contrastText",
                      },
                      flexShrink: 1,
                      minWidth: 0,
                    }}
                    onClick={() => {
                      setFile(null);
                      setPreviewUrl(null);
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        display: "inline-block",
                        width: "100%",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {file.name}
                    </Box>
                  </Button>
                  <IconButton
                    onClick={() => setIsFilterModalOpen(true)}
                    color={activeFiltersCount > 0 ? "primary" : "default"}
                    sx={{
                      minHeight: 40,
                      height: 40,
                      width: 40,
                      flexShrink: 0,
                      alignSelf: isSmallScreen ? "flex-start" : "center",
                    }}
                  >
                    <FilterAltIcon />
                    {activeFiltersCount > 0 && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: 4,
                          right: 4,
                          backgroundColor: "primary.main",
                          color: "white",
                          borderRadius: "50%",
                          width: 16,
                          height: 16,
                          fontSize: 10,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: "bold",
                        }}
                      >
                        {activeFiltersCount}
                      </Box>
                    )}
                  </IconButton>
                </Box>
              </motion.div>
            )}
          </AnimatePresence>
        </Box>
      </Stack>

      {/* Show active filters */}
      <AnimatePresence>
        {file && activeFiltersCount > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            <Box mt={1}>
              <Stack
                direction="row"
                spacing={1}
                flexWrap="wrap"
                alignItems="center"
                rowGap={0.5}
              >
                <Typography variant="caption" color="text.secondary">
                  Filters:
                </Typography>{" "}
                {Object.entries(filters).map(([key, value]) => {
                  if (
                    value === "" ||
                    value === null ||
                    value === undefined ||
                    (Array.isArray(value) && value.length === 0)
                  )
                    return null;

                  const isArray = Array.isArray(value);
                  const isString = typeof value === "string";

                  const showValues = isArray
                    ? value.slice(0, 3).join(", ")
                    : isString
                    ? value
                    : String(value);

                  const moreCount = isArray && value.length > 3 ? `, ...` : "";

                  if (key === "sku_pref") {
                    return (
                      <Chip
                        key={key}
                        label={`category: ${showValues}${moreCount}`}
                        size="small"
                        variant="outlined"
                        onDelete={() => {
                          const newFilters = { ...filters };
                          delete newFilters[key];
                          setFilters(newFilters);
                        }}
                      />
                    );
                  }
                  return (
                    <Chip
                      key={key}
                      label={`${key}: ${showValues}${moreCount}`}
                      size="small"
                      variant="outlined"
                      onDelete={() => {
                        const newFilters = { ...filters };
                        delete newFilters[key];
                        setFilters(newFilters);
                      }}
                    />
                  );
                })}
                <Button
                  size="small"
                  variant="text"
                  onClick={handleClearFilters}
                  sx={{ fontSize: "0.75rem" }}
                >
                  Clear All
                </Button>
              </Stack>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Only show catalog category for giva */}
      {previewUrl && client === "giva" && (
        <CatalogCategorySelect
          catalogCategory={catalogCategory}
          setCatalogCategory={setCatalogCategory}
        />
      )}

      {previewUrl && (
        <ClientSelect
          client={client}
          setClient={setClient}
        />
      )}

      {previewUrl && (
        <RemoveBackground
          removeBackground={removeBackground}
          setRemoveBackground={setRemoveBackground}
        />
      )}

      <AnimatePresence>
        {previewUrl && (
          <motion.div
            key="preview"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.4 }}
          >
            <Box mt={2}>
              <Box
                component="img"
                src={previewUrl}
                alt="Selected"
                sx={{
                  width: "100%",
                  maxHeight: 300,
                  objectFit: "contain",
                  borderRadius: 2,
                  border: "1px solid #ccc",
                  boxShadow: 2,
                }}
              />
            </Box>
          </motion.div>
        )}
      </AnimatePresence>

      <FilterModal
        open={isFilterModalOpen && !!file}
        onClose={() => setIsFilterModalOpen(false)}
        onApply={handleApplyFilters}
        initialFilters={filters}
        catalogCategory={catalogCategory}
        filterOptions={filterOptions}
      />

      <CameraModal
        open={isCameraModalOpen}
        onClose={() => setIsCameraModalOpen(false)}
        onCapture={handleCameraCapture}
      />
    </>
  );
}

export default ImageUploader;
